'use client';

import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import useHlsVideo, { isM3U8, bunnyCaptionUrl } from '../../utils/useHlsVideo';

// Generous buffer — this is a video the user has chosen to sit and watch.
const LIGHTBOX_HLS_CONFIG = {
  maxBufferLength: 30,
  enableWorker: true,
};

const HOVER_HIDE_DELAY = 3000;

// Bunny stores manually-uploaded captions under the language code (e.g. `en.vtt`)
// and auto-generated ones with an `-auto` suffix (e.g. `en-auto.vtt`). We probe
// each in order and use the first that exists.
const CAPTION_LANGS = ['en', 'en-auto'];

function pad2(n) {
  return (n < 10 ? '0' : '') + n;
}

function formatTime(sec) {
  if (!isFinite(sec) || sec < 0) return '00:00';
  const s = Math.floor(sec);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(r)}` : `${pad2(m)}:${pad2(r)}`;
}

/* ── Control icons (ported from the osmo reference) ───────────────────────── */
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <path
      d="M6 12V5.01109C6 4.05131 7.03685 3.4496 7.87017 3.92579L14 7.42855L20.1007 10.9147C20.9405 11.3945 20.9405 12.6054 20.1007 13.0853L14 16.5714L7.87017 20.0742C7.03685 20.5503 6 19.9486 6 18.9889V12Z"
      fill="currentColor"
    />
  </svg>
);
const PauseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <path d="M16 5V19" stroke="currentColor" strokeWidth="3" />
    <path d="M8 5V19" stroke="currentColor" strokeWidth="3" />
  </svg>
);
const VolumeOnIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <path
      d="M3 8.99998V15H7L12 20V3.99998L7 8.99998H3ZM16.5 12C16.5 10.23 15.48 8.70998 14 7.96998V16.02C15.48 15.29 16.5 13.77 16.5 12ZM14 3.22998V5.28998C16.89 6.14998 19 8.82998 19 12C19 15.17 16.89 17.85 14 18.71V20.77C18.01 19.86 21 16.28 21 12C21 7.71998 18.01 4.13998 14 3.22998Z"
      fill="currentColor"
    />
  </svg>
);
const VolumeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <path
      d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.94 18.8 13.82 18.46 14.64L19.97 16.15C20.63 14.91 21 13.5 21 12C21 7.72 18.01 4.14 14 3.23V5.29C16.89 6.15 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 18.04 14.83 18.45 14 18.7V20.76C15.38 20.45 16.63 19.81 17.69 18.95L19.73 21L21 19.73L12 10.73L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z"
      fill="currentColor"
    />
  </svg>
);
const FullscreenEnterIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <rect x="3" y="14" width="2" height="7" fill="currentColor" />
    <rect x="3" y="3" width="2" height="7" fill="currentColor" />
    <rect x="19" y="3" width="2" height="7" fill="currentColor" />
    <rect x="19" y="14" width="2" height="7" fill="currentColor" />
    <rect x="3" y="19" width="7" height="2" fill="currentColor" />
    <rect x="14" y="19" width="7" height="2" fill="currentColor" />
    <rect x="3" y="3" width="7" height="2" fill="currentColor" />
    <rect x="14" y="3" width="7" height="2" fill="currentColor" />
  </svg>
);
const FullscreenExitIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <rect x="7" y="2" width="2" height="7" fill="currentColor" />
    <rect x="15" y="2" width="2" height="7" fill="currentColor" />
    <rect x="15" y="15" width="2" height="7" fill="currentColor" />
    <rect x="8" y="15" width="2" height="7" fill="currentColor" />
    <rect x="2" y="7" width="7" height="2" fill="currentColor" />
    <rect x="3" y="15" width="7" height="2" fill="currentColor" />
    <rect x="15" y="7" width="7" height="2" fill="currentColor" />
    <rect x="15" y="15" width="7" height="2" fill="currentColor" />
  </svg>
);
const CaptionsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" width="100%">
    <path
      d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm4.5 5.2c.53 0 .96.28 1.2.72l1.3-.76C10.6 9.3 9.66 8.8 8.5 8.8A3.2 3.2 0 0 0 8.5 15.2c1.16 0 2.1-.5 2.5-1.36l-1.3-.76c-.24.44-.67.72-1.2.72a1.6 1.6 0 1 1 0-3.2Zm7 0c.53 0 .96.28 1.2.72l1.3-.76c-.4-.86-1.34-1.36-2.5-1.36a3.2 3.2 0 0 0 0 6.4c1.16 0 2.1-.5 2.5-1.36l-1.3-.76c-.24.44-.67.72-1.2.72a1.6 1.6 0 1 1 0-3.2Z"
      fill="currentColor"
    />
  </svg>
);
const LoadingIcon = () => (
  <svg viewBox="0 0 100 100" fill="none" width="100%">
    <path
      fill="currentColor"
      d="M73,50c0-12.7-10.3-23-23-23S27,37.3,27,50 M30.9,50c0-10.5,8.5-19.1,19.1-19.1S69.1,39.5,69.1,50"
    >
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="rotate"
        dur="1s"
        from="0 50 50"
        to="360 50 50"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);

/**
 * Single, app-wide video lightbox. Mounted once via VideoLightboxProvider and
 * driven entirely by props. The player chrome keys off `data-player-*`
 * attributes so the styling stays declarative (see _videoLightbox.scss).
 *
 * @param {boolean} isOpen
 * @param {string}  src    Bunny HLS (.m3u8) playlist URL
 * @param {string}  poster Placeholder image shown until first frame plays
 * @param {number}  ratio  Initial width/height ratio (refined from metadata)
 * @param {Function} onClose
 */
export default function VideoLightbox({ isOpen, src, poster, ratio, onClose }) {
  const rootRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const trackRef = useRef(null);
  const timelineRef = useRef(null);
  const progressRef = useRef(null);
  const bufferedRef = useRef(null);
  const handleRef = useRef(null);
  const rafRef = useRef(null);
  const hideTimerRef = useRef(null);
  const wantPlayRef = useRef(false);
  // Time labels are written directly to the DOM so playback never re-renders.
  const timeCurrentRef = useRef(null);
  const timeDurationRef = useRef(null);

  const [mounted, setMounted] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | ready | loading | playing | paused
  const [activated, setActivated] = useState(false);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [hover, setHover] = useState('active');
  const [dragging, setDragging] = useState(false);
  const [videoRatio, setVideoRatio] = useState(ratio || 16 / 9);
  const [hasCaptions, setHasCaptions] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);
  // Resolved same-origin proxy URL for the caption file that actually exists
  // (null until the probe below finds one). Bunny serves captions as a VTT next
  // to the playlist; we proxy it same-origin (pages/api/caption.js) so we don't
  // need `crossorigin` on the <video>.
  const [captionUrl, setCaptionUrl] = useState(null);

  useEffect(() => setMounted(true), []);

  // Attach HLS only while the lightbox is open; closing tears the buffer down.
  useHlsVideo({
    videoRef,
    src,
    enabled: isOpen && !!src,
    config: LIGHTBOX_HLS_CONFIG,
  });

  // Non-HLS sources (e.g. plain MP4) are set directly — the hook only handles HLS.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !isOpen || !src || isM3U8(src)) return;
    if (v.src !== src) {
      v.src = src;
      v.load();
    }
  }, [isOpen, src]);

  // Probe whether a caption track actually exists for this video. Setting the
  // track mode to 'hidden' triggers the browser to fetch the VTT without showing
  // it; a successful `load` reveals the CC button, an `error` (e.g. Bunny 404)
  // keeps it hidden. Re-runs whenever the source changes.
  // Resolve which caption file exists (manual `en` vs auto-generated `en-auto`)
  // by probing each candidate through the proxy; the first 200 wins and reveals
  // the CC button. Re-runs whenever the source changes.
  useEffect(() => {
    setHasCaptions(false);
    setCaptionsOn(false);
    setCaptionUrl(null);

    if (!mounted || !isM3U8(src)) return;

    let cancelled = false;
    (async () => {
      for (const lang of CAPTION_LANGS) {
        const bunny = bunnyCaptionUrl(src, lang);
        if (!bunny) continue;
        const proxy = `/api/caption?src=${encodeURIComponent(bunny)}`;
        try {
          const r = await fetch(proxy);
          const text = r.ok ? await r.text() : '';
          if (!cancelled && r.ok && text.trim().length > 0) {
            setCaptionUrl(proxy);
            setHasCaptions(true);
            return;
          }
        } catch (_) {
          // try the next candidate language
        }
        if (cancelled) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mounted, src]);

  // Once a caption URL is resolved and the <track> renders, preload it (mode
  // 'hidden') so cues display instantly on toggle.
  useEffect(() => {
    const el = trackRef.current;
    if (!captionUrl || !el) return;
    if (el.track) el.track.mode = 'hidden';
  }, [captionUrl]);

  // Start playback; if the browser blocks it, fall back to a paused state
  // instead of getting stuck on the loading spinner.
  const playVideo = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.then === 'function') p.catch(() => setStatus('paused'));
  }, []);

  /* ── Progress + time (rAF / DOM-direct so playback doesn't re-render React) ─ */
  const paintProgress = useCallback((fraction) => {
    const pct = Math.min(Math.max(fraction, 0), 1) * 100;
    if (progressRef.current)
      progressRef.current.style.transform = `translateX(${-100 + pct}%)`;
    if (handleRef.current) handleRef.current.style.left = `${pct}%`;
    // Keep the slider's reported value in sync without re-rendering React.
    if (timelineRef.current)
      timelineRef.current.setAttribute('aria-valuenow', String(Math.round(pct)));
  }, []);

  const writeCurrentTime = useCallback((seconds) => {
    if (timeCurrentRef.current)
      timeCurrentRef.current.textContent = formatTime(seconds);
  }, []);

  const stopRaf = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const startRaf = useCallback(() => {
    const loop = () => {
      const v = videoRef.current;
      if (v && v.duration) {
        paintProgress(v.currentTime / v.duration);
        writeCurrentTime(v.currentTime);
      }
      if (v && !v.paused && !v.ended)
        rafRef.current = requestAnimationFrame(loop);
    };
    stopRaf();
    rafRef.current = requestAnimationFrame(loop);
  }, [paintProgress, writeCurrentTime, stopRaf]);

  /* ── Auto-hiding controls ─────────────────────────────────────────────── */
  const wakeControls = useCallback(() => {
    setHover('active');
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setHover('idle'), HOVER_HIDE_DELAY);
  }, []);

  /* ── Playback controls ────────────────────────────────────────────────── */
  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused || v.ended) {
      wantPlayRef.current = true;
      setStatus('loading');
      playVideo();
    } else {
      v.pause();
    }
  }, [playVideo]);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  }, []);

  const toggleCaptions = useCallback(() => {
    const t = trackRef.current?.track;
    if (!t) return;
    setCaptionsOn((on) => {
      const next = !on;
      t.mode = next ? 'showing' : 'hidden';
      return next;
    });
  }, []);

  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    const v = videoRef.current;
    const fsActive =
      document.fullscreenElement || document.webkitFullscreenElement;
    if (fsActive || (v && v.webkitDisplayingFullscreen)) {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } else if (el?.requestFullscreen) {
      el.requestFullscreen();
    } else if (v?.webkitEnterFullscreen) {
      // iOS only allows native video fullscreen
      v.webkitEnterFullscreen();
    }
  }, []);

  /* ── Open / close side effects: autoplay, scroll lock, reset ──────────── */
  useEffect(() => {
    const v = videoRef.current;
    if (isOpen) {
      setVideoRatio(ratio || 16 / 9);
      wantPlayRef.current = true;
      setStatus('loading');
      wakeControls();
      // Lock background scroll (Lenis + native)
      window.lenis?.stop();
      document.body.style.overflow = 'hidden';
      // If the element is already buffered (re-open of same src), kick it off.
      if (v && v.readyState >= 3) {
        wantPlayRef.current = false;
        playVideo();
      }
    } else {
      wantPlayRef.current = false;
      stopRaf();
      window.lenis?.start();
      document.body.style.overflow = '';
      if (v) {
        try {
          v.pause();
        } catch (_) {}
      }
    }

    return () => {
      window.lenis?.start();
      document.body.style.overflow = '';
    };
  }, [isOpen, ratio, wakeControls, stopRaf, playVideo]);

  /* ── Media event wiring ───────────────────────────────────────────────── */
  // Depends on `mounted` so it (re)attaches once the <video> element exists —
  // the first render returns null, so videoRef isn't populated until then.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const writeDuration = () => {
      if (timeDurationRef.current)
        timeDurationRef.current.textContent = formatTime(v.duration || 0);
    };
    const onLoadedMeta = () => {
      writeDuration();
      if (v.videoWidth && v.videoHeight)
        setVideoRatio(v.videoWidth / v.videoHeight);
    };
    const onProgress = () => {
      if (!bufferedRef.current || !v.duration || !v.buffered.length) return;
      const end = v.buffered.end(v.buffered.length - 1);
      bufferedRef.current.style.transform = `translateX(${
        -100 + (end / v.duration) * 100
      }%)`;
    };
    const onPlay = () => {
      setActivated(true);
      setStatus('playing');
      startRaf();
    };
    const onPlaying = () => {
      wantPlayRef.current = false;
      setStatus('playing');
    };
    const onPause = () => {
      wantPlayRef.current = false;
      stopRaf();
      setStatus('paused');
    };
    const onWaiting = () => setStatus('loading');
    const onCanPlay = () => {
      if (wantPlayRef.current) {
        wantPlayRef.current = false;
        playVideo();
      } else {
        setStatus((s) => (s === 'idle' ? 'ready' : s));
      }
    };
    const onEnded = () => {
      stopRaf();
      setActivated(false);
      v.currentTime = 0;
      paintProgress(0);
      writeCurrentTime(0);
      onClose();
    };

    v.addEventListener('loadedmetadata', onLoadedMeta);
    v.addEventListener('durationchange', writeDuration);
    v.addEventListener('progress', onProgress);
    v.addEventListener('play', onPlay);
    v.addEventListener('playing', onPlaying);
    v.addEventListener('pause', onPause);
    v.addEventListener('waiting', onWaiting);
    v.addEventListener('canplay', onCanPlay);
    v.addEventListener('ended', onEnded);

    return () => {
      v.removeEventListener('loadedmetadata', onLoadedMeta);
      v.removeEventListener('durationchange', writeDuration);
      v.removeEventListener('progress', onProgress);
      v.removeEventListener('play', onPlay);
      v.removeEventListener('playing', onPlaying);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('waiting', onWaiting);
      v.removeEventListener('canplay', onCanPlay);
      v.removeEventListener('ended', onEnded);
    };
  }, [
    mounted,
    startRaf,
    stopRaf,
    paintProgress,
    writeCurrentTime,
    onClose,
    playVideo,
  ]);

  /* ── Fullscreen state sync ────────────────────────────────────────────── */
  useEffect(() => {
    const onFsChange = () =>
      setFullscreen(
        !!(document.fullscreenElement || document.webkitFullscreenElement)
      );
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  /* ── ESC to close ─────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  /* ── Focus management ─────────────────────────────────────────────────────
     When the lightbox opens, move keyboard focus into the dialog and trap it
     there so Tab / Shift+Tab cycle only the player's own controls (not the
     page behind the overlay). On close, focus returns to whatever opened it
     (e.g. the "Watch now" trigger). Required for WCAG 2.4.3 Focus Order. */
  useEffect(() => {
    if (!isOpen) return;
    const root = rootRef.current;
    if (!root) return;

    // Remember the trigger so we can restore focus to it on close.
    lastFocusedRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    const FOCUSABLE =
      'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
    const getFocusable = () =>
      Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0
      );

    // Move focus in. The close button is the reliable, always-visible anchor.
    (closeBtnRef.current || getFocusable()[0] || root).focus();

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!root.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('keydown', onKeyDown);
      const prev = lastFocusedRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [isOpen]);

  // Keep the auto-hiding controls visible while a control holds keyboard focus,
  // so a keyboard user can never focus an invisible (opacity:0) button.
  const onControlsFocus = useCallback(() => {
    setHover('active');
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  }, []);
  const onControlsBlur = useCallback(
    (e) => {
      if (!e.currentTarget.contains(e.relatedTarget)) wakeControls();
    },
    [wakeControls]
  );

  // Keyboard seeking for the timeline slider (arrows = ±5s, Home/End = ends).
  const onTimelineKeyDown = useCallback(
    (e) => {
      const v = videoRef.current;
      if (!v || !v.duration) return;
      let t = v.currentTime;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowUp':
          t = Math.min(v.currentTime + 5, v.duration);
          break;
        case 'ArrowLeft':
        case 'ArrowDown':
          t = Math.max(v.currentTime - 5, 0);
          break;
        case 'Home':
          t = 0;
          break;
        case 'End':
          t = v.duration;
          break;
        default:
          return;
      }
      e.preventDefault();
      v.currentTime = t;
      paintProgress(t / v.duration);
      writeCurrentTime(t);
    },
    [paintProgress, writeCurrentTime]
  );

  /* ── Timeline scrubbing ───────────────────────────────────────────────── */
  const seekFromEvent = useCallback((clientX) => {
    const v = videoRef.current;
    const bar = timelineRef.current;
    if (!v || !bar || !v.duration) return 0;
    const rect = bar.getBoundingClientRect();
    let f = (clientX - rect.left) / rect.width;
    f = Math.min(Math.max(f, 0), 1);
    return f;
  }, []);

  const onTimelinePointerDown = useCallback(
    (e) => {
      const v = videoRef.current;
      if (!v || !v.duration) return;
      setDragging(true);
      const wasPlaying = !v.paused && !v.ended;
      if (wasPlaying) v.pause();

      const move = (ev) => {
        const f = seekFromEvent(ev.clientX);
        paintProgress(f);
        writeCurrentTime(f * v.duration);
      };
      const up = (ev) => {
        const f = seekFromEvent(ev.clientX);
        v.currentTime = f * v.duration;
        setDragging(false);
        if (wasPlaying) playVideo();
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };

      const f = seekFromEvent(e.clientX);
      v.currentTime = f * v.duration;
      paintProgress(f);
      writeCurrentTime(f * v.duration);
      window.addEventListener('pointermove', move, { passive: true });
      window.addEventListener('pointerup', up, { passive: true });
    },
    [seekFromEvent, paintProgress, writeCurrentTime, playVideo]
  );

  if (!mounted) return null;

  const tree = (
    <div
      ref={rootRef}
      role="dialog"
      aria-modal="true"
      aria-label="Video player"
      data-bunny-lightbox-status={isOpen ? 'active' : 'not-active'}
      className="bunny-lightbox dark"
    >
      <div
        className="bunny-lightbox__dark"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="bunny-lightbox__calc"
        style={{ '--ratio-num': videoRatio }}
      >
        <div
          ref={containerRef}
          data-player-status={status}
          data-player-muted={muted}
          data-player-fullscreen={fullscreen}
          data-player-activated={activated}
          data-player-captions={captionsOn ? 'on' : 'off'}
          data-player-hover={hover}
          data-timeline-drag={dragging}
          className="bunny-lightbox-player"
          onPointerMove={wakeControls}
          onPointerLeave={() => setHover('idle')}
          onFocus={onControlsFocus}
          onBlur={onControlsBlur}
        >
          <video
            ref={videoRef}
            className="bunny-lightbox-player__video"
            playsInline
            preload="auto"
          >
            {captionUrl && (
              <track
                ref={trackRef}
                kind="subtitles"
                srcLang="en"
                label="English"
                src={captionUrl}
              />
            )}
          </video>

          {poster && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={poster}
              alt=""
              className="bunny-lightbox-player__placeholder"
            />
          )}

          <div className="bunny-lightbox-player__dark" />

          <div
            className="bunny-lightbox-player__playpause"
            onClick={togglePlay}
            aria-hidden="true"
          >
            <div className="bunny-lightbox-player__big-btn">
              {status === 'playing' ? <PauseIcon /> : <PlayIcon />}
            </div>
          </div>

          <div className="bunny-lightbox-player__interface">
            <div className="bunny-lightbox-player__interface-fade" />
            <div className="bunny-lightbox-player__interface-bottom">
              <button
                type="button"
                className="bunny-lightbox-player__toggle-playpause"
                onClick={togglePlay}
                aria-label={status === 'playing' ? 'Pause' : 'Play'}
              >
                {status === 'playing' ? <PauseIcon /> : <PlayIcon />}
              </button>

              <div className="bunny-lightbox-player__time">
                <p ref={timeCurrentRef} className="bunny-lightbox-player__text">
                  00:00
                </p>
                <p className="bunny-lightbox-player__text is--transparent">/</p>
                <p
                  ref={timeDurationRef}
                  className="bunny-lightbox-player__text is--transparent"
                >
                  00:00
                </p>
              </div>

              <div
                ref={timelineRef}
                className="bunny-lightbox-player__timeline"
                onPointerDown={onTimelinePointerDown}
                onKeyDown={onTimelineKeyDown}
                role="slider"
                tabIndex={0}
                aria-label="Seek"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={0}
              >
                <div className="bunny-lightbox-player__timeline-bar">
                  <div className="bunny-lightbox-player__timeline-bg" />
                  <div
                    ref={bufferedRef}
                    className="bunny-lightbox-player__timeline-buffered"
                  />
                  <div
                    ref={progressRef}
                    className="bunny-lightbox-player__timeline-progress"
                  />
                </div>
                <div
                  ref={handleRef}
                  className="bunny-lightbox-player__timeline-handle"
                />
              </div>

              <div className="bunny-lightbox-player__interface-btns">
                {hasCaptions && (
                  <button
                    type="button"
                    className="bunny-lightbox-player__toggle-captions"
                    onClick={toggleCaptions}
                    aria-pressed={captionsOn}
                    aria-label="Captions"
                  >
                    <CaptionsIcon />
                  </button>
                )}
                <button
                  type="button"
                  className="bunny-lightbox-player__toggle-mute"
                  onClick={toggleMute}
                  aria-label={muted ? 'Unmute' : 'Mute'}
                >
                  {muted ? <VolumeOffIcon /> : <VolumeOnIcon />}
                </button>
                <button
                  type="button"
                  className="bunny-lightbox-player__toggle-fullscreen"
                  onClick={toggleFullscreen}
                  aria-label={fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {fullscreen ? (
                    <FullscreenExitIcon />
                  ) : (
                    <FullscreenEnterIcon />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="bunny-lightbox-player__loading">
            <span className="bunny-lightbox-player__loading-svg">
              <LoadingIcon />
            </span>
          </div>
        </div>
      </div>

      <button
        ref={closeBtnRef}
        type="button"
        className="bunny-lightbox__close"
        onClick={onClose}
        aria-label="Close video"
      >
        <div className="bunny-lightbox__close-bar" />
        <div className="bunny-lightbox__close-bar is--duplicate" />
      </button>
    </div>
  );

  return createPortal(tree, document.body);
}
